/**
 * Analytics Provider - Loads GA4 and Meta Pixel dynamically
 * Provides event tracking functions for the entire app
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analytics Context
const AnalyticsContext = createContext({
  trackEvent: () => {},
  trackConversion: () => {},
  trackPageView: () => {},
  isReady: false
});

export const useAnalytics = () => useContext(AnalyticsContext);

// Analytics Provider Component
export const AnalyticsProvider = ({ children }) => {
  const [settings, setSettings] = useState({ ga4_measurement_id: '', meta_pixel_id: '' });
  const [isReady, setIsReady] = useState(false);

  // Fetch analytics settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/analytics`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.error('Error fetching analytics settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Load Google Analytics 4
  useEffect(() => {
    if (!settings.ga4_measurement_id) return;

    // Check if already loaded
    if (window.gtag) {
      setIsReady(true);
      return;
    }

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${settings.ga4_measurement_id}`;
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', settings.ga4_measurement_id, {
        page_path: window.location.pathname,
        cookie_flags: 'SameSite=None;Secure'
      });
      setIsReady(true);
    };

    return () => {
      // Cleanup if needed
    };
  }, [settings.ga4_measurement_id]);

  // Load Meta Pixel
  useEffect(() => {
    if (!settings.meta_pixel_id) return;

    // Check if already loaded
    if (window.fbq) return;

    // Load Facebook Pixel
    (function(f,b,e,v,n,t,s) {
      if(f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if(!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', settings.meta_pixel_id);
    window.fbq('track', 'PageView');
  }, [settings.meta_pixel_id]);

  // Track page views on route change
  useEffect(() => {
    const handleRouteChange = () => {
      // GA4 page view
      if (window.gtag && settings.ga4_measurement_id) {
        window.gtag('config', settings.ga4_measurement_id, {
          page_path: window.location.pathname
        });
      }
      // Meta Pixel page view
      if (window.fbq) {
        window.fbq('track', 'PageView');
      }
    };

    // Listen for history changes
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [settings.ga4_measurement_id]);

  // Track page view manually (call from route changes)
  const trackPageView = useCallback((path) => {
    // GA4 page view
    if (window.gtag && settings.ga4_measurement_id) {
      window.gtag('config', settings.ga4_measurement_id, {
        page_path: path || window.location.pathname
      });
    }
    // Meta Pixel page view
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [settings.ga4_measurement_id]);

  // Generic event tracking
  const trackEvent = useCallback((eventName, params = {}) => {
    // GA4 event
    if (window.gtag) {
      window.gtag('event', eventName, params);
    }
    // Meta Pixel custom event
    if (window.fbq) {
      window.fbq('trackCustom', eventName, params);
    }
  }, []);

  // Conversion tracking for specific events
  const trackConversion = useCallback((conversionType, data = {}) => {
    const conversionEvents = {
      // Lead form submission
      lead_form_submit: {
        ga4: { event: 'generate_lead', params: { ...data, method: 'homepage_form' } },
        meta: { event: 'Lead', params: data }
      },
      // WhatsApp click
      whatsapp_click: {
        ga4: { event: 'contact', params: { ...data, method: 'whatsapp' } },
        meta: { event: 'Contact', params: { ...data, method: 'whatsapp' } }
      },
      // Phone click
      phone_click: {
        ga4: { event: 'contact', params: { ...data, method: 'phone' } },
        meta: { event: 'Contact', params: { ...data, method: 'phone' } }
      },
      // Email click
      email_click: {
        ga4: { event: 'contact', params: { ...data, method: 'email' } },
        meta: { event: 'Contact', params: { ...data, method: 'email' } }
      },
      // Property view
      property_view: {
        ga4: { event: 'view_item', params: data },
        meta: { event: 'ViewContent', params: data }
      },
      // Blog view
      blog_view: {
        ga4: { event: 'page_view', params: { ...data, content_type: 'blog' } },
        meta: { event: 'ViewContent', params: { ...data, content_type: 'article' } }
      }
    };

    const config = conversionEvents[conversionType];
    if (!config) {
      console.warn(`Unknown conversion type: ${conversionType}`);
      return;
    }

    // GA4 conversion
    if (window.gtag && config.ga4) {
      window.gtag('event', config.ga4.event, config.ga4.params);
    }

    // Meta Pixel conversion
    if (window.fbq && config.meta) {
      window.fbq('track', config.meta.event, config.meta.params);
    }
  }, []);

  return (
    <AnalyticsContext.Provider value={{ trackEvent, trackConversion, trackPageView, isReady }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Hook to track page views on route changes (use inside Router)
export const usePageViewTracking = () => {
  const { trackPageView } = useAnalytics();
  const prevPath = useRef(window.location.pathname);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (prevPath.current !== currentPath) {
      trackPageView(currentPath);
      prevPath.current = currentPath;
    }
  });
};

export default AnalyticsProvider;
